/* eslint-disable react/no-unknown-property */
import React, { useMemo, useState } from "react";
import {
  isToDoListInFuture,
  todoListInProgress,
  todoListMarkCompleteTask,
  todoListMarkInCompleteTask,
  todoListSkipTask
} from "./todo_list_util";
import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import PropTypes from "prop-types";
import SeenCheckbox from "../seen_checkbox";
import { START_HOURS, shiftLabel } from "../../shift";

const selectOptions = ["skip occurrence", "pass to next shift"];

const confirmationTitles = {
  complete: "Confirm completion",
  incomplete: "Mark task as incomplete",
  skip: "Skip recurring task"
};

export const TODOListActionButton = (props) => {
  const {
    toDoListTitle,
    shiftNumber,
    todoListId,
    todoListDate,
    time: unsafeTime,
    type,
    recurrence_start_date,
    state
  } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const [reason, setReason] = useState(undefined);
  const [userInitial, setUserInitial] = useState("");
  const [seen, setSeen] = useState("0");
  const [skipOccurrenceType, setSkipOccurrenceType] = useState("");

  const safeToDoListTime = useMemo(() => {
    if (unsafeTime) return unsafeTime;
    for (const number of [1, 2, 3]) {
      if (shiftNumber === shiftLabel(number)) {
        return `${todoListDate} ${String(START_HOURS[number]).padStart(2, "0")}:00:00`;
      }
    }
  }, [unsafeTime, shiftNumber, todoListDate]);

  const todoListIsInPresentOrPast = useMemo(() => {
    return isToDoListInFuture(safeToDoListTime, todoListDate);
  }, [safeToDoListTime, todoListDate]);

  const markAsComplete = (event) => {
    if (event.preventDefault) event.preventDefault();
    switch (type) {
      case "complete":
        todoListMarkCompleteTask(
          todoListId,
          todoListDate,
          safeToDoListTime,
          userInitial,
          reason,
          seen
        );
        break;
      case "incomplete":
        todoListMarkInCompleteTask(
          todoListId,
          todoListDate,
          safeToDoListTime,
          userInitial,
          reason,
          seen
        );
        break;
      case "skip":
        todoListSkipTask(
          todoListId,
          todoListDate,
          safeToDoListTime,
          seen,
          skipOccurrenceType,
          userInitial,
          reason
        );
        break;
    }
  };

  const buttonContentStyleAndIcon = useMemo(() => {
    switch (type) {
      case "complete":
        return {
          label: "Mark as completed",
          style:
            "button ccw-button ccw-button--primary to-do-list-action-button-base to-do-list-complete-button todo-action-button todo-action-button--complete",
          icon: "far fa-check-circle"
        };
      case "incomplete":
        return {
          label: "Mark as incomplete",
          style:
            "button ccw-button ccw-button--warning to-do-list-action-button-base to-do-list-incomplete-button todo-action-button todo-action-button--incomplete",
          icon: "fas fa-not-equal"
        };
      case "skip":
        return {
          label: "Skip occurrence",
          style:
            "button ccw-button ccw-button--warning to-do-list-action-button-base to-do-list-skip-occurrence-button todo-action-button todo-action-button--skip",
          icon: "far fa-clock"
        };
      case "inprogress":
        return {
          label: "Mark as in progress",
          icon: "far fa-clock",
          style:
            "button ccw-button to-do-list-action-button-base to-do-list-in-progress-button todo-action-button todo-action-button--inprogress"
        };
    }
  }, [type]);

  const onActionButtonClicked = () => {
    if (type === "inprogress") {
      todoListInProgress(todoListId);
    } else {
      if (type === "skip") setSkipOccurrenceType("");
      setModalOpen(true);
    }
  };

  const onSetSeen = () => {
    if (seen === "1") setSeen("0");
    if (seen === "0") setSeen("1");
  };

  const shouldRenderButton = () => {
    if (!state && type === "inprogress") return false;
    if (state === "completed" && type === "complete") return false;
    if (state === "incomplete" && type === "incomplete") return false;
    if (state === "skipped" && type === "skip") return false;
    if (type === "skip" && !recurrence_start_date) return false;
    return true;
  };

  if (!shouldRenderButton()) return <></>;
  return (
    <>
      <TODOListActionButtonConfirmationModal
        skipOccurrenceType={skipOccurrenceType}
        setSkipOccurrenceType={setSkipOccurrenceType}
        seen={seen === "1"}
        setSeen={onSetSeen}
        type={type}
        handleSubmit={markAsComplete}
        reason={reason}
        setReason={setReason}
        userInitial={userInitial}
        setUserInitial={setUserInitial}
        modalOpen={modalOpen}
        handleClose={() => setModalOpen(false)}
        todoListIsInPresentOrPast={todoListIsInPresentOrPast}
      />
      <button
        explorator_to_do_list_test_id_title={`to_do_list_action_button_${type}_${toDoListTitle}`}
        explorator_test_id={`to_do_list_action_button_${type}`}
        className={buttonContentStyleAndIcon.style}
        onClick={onActionButtonClicked}
      >
        <i className={buttonContentStyleAndIcon.icon} aria-hidden="true"></i>
        <span>{buttonContentStyleAndIcon.label}</span>
      </button>
    </>
  );
};

TODOListActionButton.propTypes = {
  todoListId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  todoListDate: PropTypes.string,
  time: PropTypes.string,
  type: PropTypes.string, // can be: complete, incomplete, skip, inprogress
  isComplete: PropTypes.bool,
  isInComplete: PropTypes.bool,
  shiftNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  toDoListTitle: PropTypes.string,
  state: PropTypes.string,
  recurrence_start_date: PropTypes.string
};

const TODOListActionButtonConfirmationModal = ({
  skipOccurrenceType,
  setSkipOccurrenceType,
  seen,
  setSeen,
  type,
  handleClose,
  handleSubmit,
  modalOpen,
  userInitial,
  setUserInitial,
  setReason,
  reason,
  todoListIsInPresentOrPast
}) => {
  if (!modalOpen) return <></>;
  const reasonRequired =
    type === "incomplete" || type === "skip" || (type === "complete" && !todoListIsInPresentOrPast);
  const dialogTitle = confirmationTitles[type] || "Confirm task update";

  return (
    <Dialog
      open={modalOpen}
      onClose={handleClose}
      aria-labelledby="ccw-todo-confirmation-title"
      aria-describedby={
        type === "complete" && !todoListIsInPresentOrPast
          ? "ccw-todo-confirmation-warning"
          : undefined
      }
      scroll="paper"
      PaperProps={{
        className: "ccw-dialog ccw-shared-dialog ccw-todo-confirmation-dialog"
      }}
      BackdropProps={{ className: "ccw-shared-dialog__backdrop" }}
    >
      <DialogTitle component="div" className="ccw-dialog__header ccw-shared-dialog__header">
        <h2 id="ccw-todo-confirmation-title" className="ccw-shared-dialog__title">
          {dialogTitle}
        </h2>
        <IconButton
          className="ccw-shared-dialog__close"
          aria-label="Close confirmation"
          onClick={handleClose}
        >
          <CloseIcon aria-hidden="true" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="ccw-dialog__body ccw-shared-dialog__body ccw-todo-confirmation-dialog__body">
        <form className="ccw-todo-confirmation-form" onSubmit={handleSubmit}>
          <div className="ccw-field">
            <label className="ccw-field__label" htmlFor="ccw-todo-confirmation-initials">
              Initials
            </label>
            <input
              id="ccw-todo-confirmation-initials"
              type="text"
              className="ccw-input"
              name="userInitialInput"
              value={userInitial}
              onChange={(e) => setUserInitial(e.target.value)}
              explorator_test_id={"to_do_list_action_button_model_initials_input"}
              autoFocus
            />
          </div>
          {reasonRequired && (
            <>
              {type === "complete" && !todoListIsInPresentOrPast && (
                <div
                  id="ccw-todo-confirmation-warning"
                  className="ccw-notice ccw-notice--warning ccw-todo-confirmation-warning"
                >
                  <WarningIcon className="warning-icon" aria-hidden="true" />
                  <p>
                    This task is scheduled for the future. Confirm the work was completed before its
                    scheduled time.
                  </p>
                </div>
              )}
              <div className="ccw-field">
                <label className="ccw-field__label" htmlFor="ccw-todo-confirmation-reason">
                  Reason
                </label>
                <textarea
                  id="ccw-todo-confirmation-reason"
                  className="ccw-textarea"
                  name="reason"
                  value={reason || ""}
                  onChange={(e) => setReason(e.target.value)}
                  explorator_test_id={"to_do_list_action_button_model_reason_input"}
                ></textarea>
              </div>
            </>
          )}
          <div className="ccw-todo-confirmation-seen">
            <SeenCheckbox className="ccw-seen-options" seen={seen} setSeen={setSeen} />
          </div>
          {type === "skip" && (
            <div className="ccw-field">
              <label className="ccw-field__label" htmlFor="ccw-todo-confirmation-skip-type">
                What should happen next?
              </label>
              <select
                id="ccw-todo-confirmation-skip-type"
                className="ccw-select pass-to-next-shift-options-select"
                value={skipOccurrenceType}
                onChange={(e) => setSkipOccurrenceType(e.target.value)}
                required
                explorator_test_id={"to_do_list_action_button_model_select_skip_type"}
              >
                <option value="" disabled>
                  Choose action
                </option>
                {selectOptions.map((option) => {
                  return (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
          <div className="ccw-shared-dialog__actions">
            <button type="button" className="ccw-button" onClick={handleClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="ccw-button ccw-button--primary mark-as-completed-button"
              disabled={
                !userInitial ||
                (type === "skip" && !skipOccurrenceType) ||
                (reasonRequired && !reason)
              }
              explorator_test_id={"to_do_list_action_button_model_submit_button"}
            >
              Confirm
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

TODOListActionButtonConfirmationModal.propTypes = {
  handleClose: PropTypes.func,
  handleSubmit: PropTypes.func,
  modalOpen: PropTypes.bool,
  requireReason: PropTypes.bool,
  userInitial: PropTypes.string,
  setUserInitial: PropTypes.func,
  setReason: PropTypes.func,
  reason: PropTypes.string,
  type: PropTypes.string,
  seen: PropTypes.bool,
  setSeen: PropTypes.func,
  skipOccurrenceType: PropTypes.string,
  setSkipOccurrenceType: PropTypes.func,
  todoListIsInPresentOrPast: PropTypes.bool
};
